'use client'

import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { Cell, Divider, Label, Radio, Text } from '@vinted/web-ui'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'
import { List } from '@marketplace-web/common-components/list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  NationalityCountryModel,
  getNationalities,
} from '@marketplace-web/nationality-select/nationality-select-data'

type Props = {
  title: string
  placeholder: string
  name: string
  validation: ReactNode
  showSuggestedCountries?: boolean
  disabled?: boolean
  value?: NationalityCountryModel | null
  styling?: ComponentProps<typeof InputDropdown>['styling']
  onChange: (country: NationalityCountryModel | null) => void
}

const NationalitySelect = ({
  title,
  placeholder,
  name,
  validation,
  showSuggestedCountries,
  disabled,
  value,
  styling,
  onChange,
}: Props) => {
  const translate = useTranslate('nationality_select')
  const inputDropdownTranslate = useTranslate()
  const breakpoints = useBreakpoint()

  const [state, setState] = useState(UiState.Idle)
  const [countries, setCountries] = useState<Array<NationalityCountryModel>>([])
  const [suggestedCountries, setSuggestedCountries] = useState<Array<NationalityCountryModel>>([])
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<NationalityCountryModel | null>(
    value || null,
  )

  useEffect(() => {
    if (!value) return

    setSelectedCountry(value)
  }, [value, setSelectedCountry])

  useEffect(() => {
    const fetchNationalities = async () => {
      setState(UiState.Pending)

      const response = await getNationalities()

      if ('errors' in response) {
        setState(UiState.Failure)

        return
      }

      const remoteCountries = response.countries.map(country => ({
        code: country.code,
        title: country.title,
      }))

      const remoteSuggestedCountries = response.suggested_countries.map(country => ({
        code: country.code,
        title: country.title,
      }))

      setCountries(remoteCountries)
      setSuggestedCountries(remoteSuggestedCountries)
      setState(UiState.Success)
    }

    fetchNationalities()
  }, [])

  const resetCountryData = () => {
    setSearchTerm(null)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.currentTarget.value)
  }

  const handleCountryChange =
    (country: NationalityCountryModel, inputRenderProps: InputDropdownRenderProps) => () => {
      inputRenderProps.setIsSaveEnabled(true)

      if (breakpoints.phones) {
        setSelectedCountry(country)
        onChange(country)

        return
      }

      setSelectedCountry(country)
      onChange(country)

      inputRenderProps.closeDropdown()
    }

  const handleSave = () => {
    setSelectedCountry(selectedCountry)
    onChange(selectedCountry)
    resetCountryData()
  }

  const renderCountry = (
    key: string,
    country: NationalityCountryModel,
    inputRenderProps: InputDropdownRenderProps,
  ) => {
    return (
      <Cell
        key={country.code}
        title={
          <Text as="p" type={Text.Type.Title} text={country.title} id={`country-select-${key}`} />
        }
        suffix={
          <Radio
            name={`${key}-radio`}
            checked={country.code === selectedCountry?.code}
            value={country.code}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': `country-select-${key}`,
            }}
          />
        }
        onClick={handleCountryChange(country, inputRenderProps)}
      />
    )
  }

  const renderSuggestedCountries = (inputRenderProps: InputDropdownRenderProps) => {
    if (!showSuggestedCountries || !!searchTerm || suggestedCountries.length === 0) return null

    return (
      <>
        <Label type={Label.Type.Leading} text={translate('suggested_countries.title')} />
        <List>
          {suggestedCountries.map(country =>
            renderCountry(`suggested-country-${country.code}`, country, inputRenderProps),
          )}
        </List>
        <Divider />
      </>
    )
  }

  const renderOtherCountries = (inputRenderProps: InputDropdownRenderProps) => {
    const filteredCountries = searchTerm
      ? countries.filter(country => country.title.toLowerCase().includes(searchTerm.toLowerCase()))
      : countries

    return (
      <>
        {showSuggestedCountries && !searchTerm && (
          <Label type={Label.Type.Leading} text={translate('other_countries.title')} />
        )}
        <List>
          {filteredCountries.map(country =>
            renderCountry(`country-${country.code}`, country, inputRenderProps),
          )}
        </List>
      </>
    )
  }

  const renderCountries = (inputRenderProps: InputDropdownRenderProps) => {
    return (
      <>
        {renderSuggestedCountries(inputRenderProps)}
        {renderOtherCountries(inputRenderProps)}
      </>
    )
  }

  if (state !== UiState.Success) return <ContentLoader styling={ContentLoader.Styling.Wide} />

  return (
    <InputDropdown
      isPhone={!!breakpoints.phones}
      textSave={inputDropdownTranslate('actions.save')}
      name={name}
      styling={styling}
      title={title}
      placeholder={placeholder}
      value={selectedCountry?.title}
      render={renderCountries}
      disabled={disabled}
      onChange={handleInputChange}
      onCancel={resetCountryData}
      onClose={resetCountryData}
      onSave={handleSave}
      validation={validation}
    />
  )
}

export default NationalitySelect
